/*
  Seitenpanel mit allen Planungs-Konflikten des sichtbaren Zeitraums.
  Dockt rechts neben dem Raster an; Hover auf einen Konflikt hebt dessen
  Einträge im Schichtplan hervor (gleicher Mechanismus wie die Freigabe-Vorschau).
*/

import React, { useMemo } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import JoinInnerOutlinedIcon from "@mui/icons-material/JoinInnerOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import { IconClose, IconHoliday, IconSuccess, IconWarning } from "../../../../design-system/icons";
import { CONFLICT_TYPES, getConflictTypeLabel } from "../../domain/model/conflicts";

const CONFLICT_TYPE_ICONS = Object.freeze({
  [CONFLICT_TYPES.SHIFT_OVERLAP]: JoinInnerOutlinedIcon,
  [CONFLICT_TYPES.SHIFT_ON_ABSENCE]: EventBusyOutlinedIcon,
  [CONFLICT_TYPES.SHIFT_ON_HOLIDAY]: IconHoliday,
  [CONFLICT_TYPES.SHIFT_AGAINST_AVAILABILITY]: PersonOffOutlinedIcon,
});

const DATE_LABEL_FORMATTER = new Intl.DateTimeFormat("de-CH", {
  weekday: "short",
  day: "2-digit",
  month: "2-digit",
});

function formatConflictDateLabel(dateKey) {
  const match = String(dateKey || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return String(dateKey || "");

  const [, year, month, day] = match;
  return DATE_LABEL_FORMATTER.format(new Date(Number(year), Number(month) - 1, Number(day)));
}

function ConflictTypeIcon({ type }) {
  const Icon = CONFLICT_TYPE_ICONS[type] || IconWarning;
  return <Icon fontSize="small" />;
}

export default function ConflictsPanel({ open, conflictModel, periodLabel, onConflictHover, onClose }) {
  const conflicts = useMemo(
    () => (Array.isArray(conflictModel?.conflicts) ? conflictModel.conflicts : []),
    [conflictModel]
  );
  const hasConflicts = conflicts.length > 0;

  if (!open) return null;

  return (
    <Box className="spv2-conflicts-panel" role="complementary" aria-label="Konflikte">
      <Box className="spv2-conflicts-panel-header">
        <Box className="spv2-conflicts-panel-header-main">
          <Box className="spv2-conflicts-panel-header-icon" aria-hidden="true">
            <IconWarning fontSize="small" />
          </Box>
          <Box className="spv2-conflicts-panel-title-copy">
            <Typography className="spv2-conflicts-panel-title">Konflikte</Typography>
            <Typography className="spv2-conflicts-panel-subtitle">{periodLabel}</Typography>
          </Box>
        </Box>
        <Box className="spv2-conflicts-panel-header-actions">
          <span className="spv2-conflicts-panel-count" aria-label={`${conflicts.length} Konflikte`}>
            {conflicts.length}
          </span>
          <IconButton
            className="spv2-floating-panel-close-btn"
            size="small"
            aria-label="Konflikte schließen"
            onClick={onClose}
          >
            <IconClose fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {hasConflicts ? (
        <Box
          className="spv2-conflicts-panel-list"
          role="list"
          aria-label="Konflikte"
          onMouseLeave={() => onConflictHover?.(null)}
        >
          {conflicts.map((conflict) => (
            <Box
              key={conflict.id}
              className={`spv2-conflict-row is-${conflict.type}`}
              role="listitem"
              onMouseEnter={() => onConflictHover?.(conflict)}
            >
              <span className="spv2-conflict-row-icon" aria-hidden="true">
                <ConflictTypeIcon type={conflict.type} />
              </span>
              <Box className="spv2-conflict-row-copy">
                <Box className="spv2-conflict-row-top">
                  <span className="spv2-conflict-row-name">{conflict.employeeName || "Unbekannt"}</span>
                  <span className="spv2-conflict-row-date">{formatConflictDateLabel(conflict.dateKey)}</span>
                </Box>
                <span className="spv2-conflict-row-type">{getConflictTypeLabel(conflict.type)}</span>
                <span className="spv2-conflict-row-detail">{conflict.detail}</span>
              </Box>
            </Box>
          ))}
        </Box>
      ) : (
        <Box className="spv2-conflicts-panel-empty">
          <Box className="spv2-conflicts-panel-empty-icon" aria-hidden="true">
            <IconSuccess fontSize="small" />
          </Box>
          <Typography className="spv2-conflicts-panel-empty-text">
            Alle Schichten im Zeitraum sind konfliktfrei geplant.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
